'use client';
import { memo, useEffect, useMemo, useState } from 'react';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

import { formatISOToDate } from '@utils/dateUtil';
import useBasketInformation from '@hooks/useBasketInformation';
import ScrollToTop from '@components/ScrollToTop/ScrollToTop';
import { useIsClient } from '@utils/next';
import { useDevice } from '@contexts/device';
import BookerNext from '@components/views/landing/components/bookerNext/Booker';
import Booker from '@components/views/landing/components/booker/Booker';
import { CampaignPromotions } from '@components/views/landing/components/campaign/CampaignPromotions/CampaignPromotions';
import { useAuthState } from '@contexts/auth/AuthContext';
import { useHotelDifference } from '@contexts/hotel-diff/HotelDifferenceContext';
import { useInsiderBannerContext } from '@contexts/insiderBanners';
import { useHideChatGPT } from '@components/ChatGPTLoader/hooks';
import { SessionScopeGTM } from '@utils/gtm/session-scope';

import {
  Containers,
  Container,
  LandingContainer,
  SeoTextContainer,
  HeroImageContainer,
  HomeBookerWrapper,
  CampaignPromotionsContainer,
  WhySeturContainer,
  ContainersMobil,
  HomeBookerWrapperMobile,
  ImageDiv,
} from './HomeStyle';
import { WhySeturItemProps } from './components/WhyBuyingFromSetur';
import useAffiliation from '../hooks/useAffiliation';
import { HomepageProps } from './props';
import {
  useGetFavoriteListItems,
  useGetFavoriteLists,
} from '../my-favorites/hooks';
import { findDefaultList } from '../my-favorites/MyFavoriteComponent/utils';

const SeturStories = dynamic(() => import('./components/SeturStories'));

const GeneralLoader = dynamic(() => import('@components/ui/GeneralLoader'), {
  ssr: false,
});

const MostPreferredHotels = dynamic(
  () => import('./components/MostPreferredHotels')
);

const PlanVacation = dynamic(
  () => import('../landing/components/plan-vacation/PlanVacation'),
  { ssr: false }
);

const FeaturedTours = dynamic(() => import('./components/FeaturedTours'));

const WhyBuyingFromSetur = dynamic(
  () => import('./components/WhyBuyingFromSetur')
);

const MostPreferredThemes = dynamic(
  () => import('./components/MostPreferredThemes')
);

const HomePageSeo = dynamic(
  () => import('../landing/components/seoText/homePageSeo')
);

const MobileAppRedirection = dynamic(
  () => import('@components/AppStoreRedirection/AppStoreRedirection')
);

const Index = ({
  campaignPromotions,
  resMainPageSeo,
  whyBuyingFromSeturData,
  heroParallaxData,
  mobileAppPromotions,
  mostPreferredHotels,
  preferredThemes,
  featuredToursData,
}: HomepageProps) => {
  const { basketInfo, itemsInBasket, basketId } = useBasketInformation();

  const path = usePathname() || '/';
  const [isBookerLoading, setIsBookerLoading] = useState<boolean>(false);
  const isClient = useIsClient();
  const { isDesktop } = useDevice();

  SessionScopeGTM('Anasayfa', '');

  const showMostPreferredHotels =
    mostPreferredHotels &&
    mostPreferredHotels?.hotels?.length &&
    mostPreferredHotels?.categories?.length;
  useAffiliation(path);
  useHideChatGPT(false);
  const { setVisibleInHeader } = useHotelDifference();
  const { setPuanExtraBannerStatus } = useInsiderBannerContext();

  useEffect(() => {
    setVisibleInHeader(true);
    setPuanExtraBannerStatus(false);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setPuanExtraBannerStatus(true);
      }
      if (window.scrollY === 0) {
        setPuanExtraBannerStatus(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    return () => {
      setVisibleInHeader(false);
      setPuanExtraBannerStatus(false);
    };
  }, []);

  const basketHotelInfo = useMemo(() => {
    if (!basketInfo || !basketInfo.hotels) return [];
    if (basketInfo?.hotels?.length) {
      return basketInfo?.hotels?.map((hotel) => ({
        name: hotel.name || '',
        city: hotel.city || '',
        region: hotel.region || '',
        in: formatISOToDate(hotel.in) || '',
        out: formatISOToDate(hotel.out) || '',
        boardType: hotel.roomName || '',
        adultCount: hotel.guests?.adultCount || 0,
        childCount:
          (hotel.guests?.childCount ?? 0) + (hotel.guests?.infCount ?? 0),
      }));
    }
    return [];
  }, [basketInfo]);

  const whySeturData = useMemo(() => {
    const items: WhySeturItemProps[] = [];
    whyBuyingFromSeturData?.items?.map((item) =>
      items.push({
        image: item?.images?.[0],
        label: item?.title || '',
        description: item?.text || '',
      })
    );

    return items;
  }, [whyBuyingFromSeturData]);

  const basketTourInfo = useMemo(() => {
    if (!basketInfo || !basketInfo.tours) return [];
    if (basketInfo?.tours?.length) {
      return basketInfo?.tours?.map((tour) => ({
        name: tour.name || '',
        departureDate: formatISOToDate(tour.departureDate) || '',
        arrivalDate: formatISOToDate(tour.arrivalDate) || '',
        adultCount: tour.guests?.adultCount ?? 0,
        childCount:
          (tour.guests?.childCount ?? 0) + (tour.guests?.infCount ?? 0),
        tourTransportationTypeName: tour.tourTransportationTypeName || '',
        tourDepartureDate: tour.departureDate,
        tourArrivalDate: tour.arrivalDate,
        tourType: tour.tourType,
      }));
    }
    return [];
  }, [basketInfo]);

  const basketFlightInfo = useMemo(() => {
    const items: any = [];
    if (basketInfo?.flights?.length) {
      basketInfo?.flights?.forEach((flight) => {
        if (flight.plan?.flights?.length > 1) {
          const departureFlight = flight.plan.flights[0];
          const arrivalFlight = flight.plan.flights[1];
          items.push({
            name: `${departureFlight?.segments?.[0]?.depCode} - ${departureFlight?.segments?.[0]?.arrCode}`,
            type: 'Gidiş - Dönüş',
            departureDate: departureFlight?.segments?.[0]?.depDate || '',
            arrivalDate: arrivalFlight?.segments?.[0]?.depDate || '',
            adultCount: flight?.guests?.adultCount || 0,
            childCount: flight?.guests?.childCount || 0,
          });
        } else {
          const departureFlight = flight.plan.flights[0];
          items.push({
            name: `${departureFlight?.segments?.[0]?.depCode} - ${departureFlight?.segments?.[0]?.arrCode}`,
            type: 'Tek Yön',
            departureDate: departureFlight?.segments?.[0]?.depDate || '',
            arrivalDate: departureFlight?.segments?.[0]?.arrDate || '',
            adultCount: flight?.guests?.adultCount || 0,
            childCount: flight?.guests?.childCount || 0,
          });
        }
      });
    }
    return items;
  }, [basketInfo]);

  const basketHref = useMemo(
    () => (basketId ? `seyahat-planlama?bid=${basketId}` : 'seyahat-planlama'),
    [basketId]
  );

  const { loginInfo } = useAuthState();
  const { favoriteLists, refetchFavoriteLists } = useGetFavoriteLists({
    token: loginInfo?.token ?? '',
  });

  const defaultList = useMemo(() => {
    return findDefaultList(favoriteLists);
  }, [favoriteLists]);

  const {
    favoriteListItems,
    refetchFavoriteListItems,
    isFavoriteListItemError,
  } = useGetFavoriteListItems({
    wishListId: defaultList?.id ?? '',
    size: 1000,
  });
  const [isRedirecting, isRedirectingSet] = useState(false);
  const devicePixelRatio = isClient ? window.devicePixelRatio : 0;
  return (
    <>
      <ScrollToTop />
      {isDesktop ? (
        <Containers>
          <HeroImageContainer>
            <ImageDiv isDesktop={isDesktop}>
              <Image
                src={
                  heroParallaxData?.images?.[0]?.url ||
                  '/static/images/hero-bg.svg'
                }
                fill
                style={{ objectFit: 'initial' }}
                alt={heroParallaxData?.images[0]?.alt ?? ''}
                priority
              />
            </ImageDiv>
          </HeroImageContainer>

          <HomeBookerWrapper>
            <BookerNext setIsBookerLoading={setIsBookerLoading} />
          </HomeBookerWrapper>
        </Containers>
      ) : (
        <ContainersMobil>
          <HomeBookerWrapperMobile>
            <Booker
              setIsBookerLoading={setIsBookerLoading}
              bottom={devicePixelRatio > 1 ? 200 : 400}
            />
          </HomeBookerWrapperMobile>
        </ContainersMobil>
      )}

      {isDesktop ? null : <SeturStories />}

      {itemsInBasket > 0 ? (
        <PlanVacation
          hotels={basketHotelInfo || []}
          tours={basketTourInfo || []}
          flights={basketFlightInfo}
          href={basketHref}
          isRedirectingSet={isRedirectingSet}
          isPackageBasket={basketInfo?.isPackageBasket}
        />
      ) : null}

      {campaignPromotions ? (
        <CampaignPromotionsContainer>
          <CampaignPromotions
            data={campaignPromotions.data}
            link={campaignPromotions.link}
            imagesKey='homePageImages'
            text={campaignPromotions.text}
          />
        </CampaignPromotionsContainer>
      ) : null}
      <Container>
        <LandingContainer>
          {showMostPreferredHotels ? (
            <MostPreferredHotels
              isRedirectingSet={isRedirectingSet}
              tabs={mostPreferredHotels?.categories}
              hotelItems={mostPreferredHotels?.hotels}
              content={mostPreferredHotels?.content}
              favoriteLists={favoriteLists}
              refetchFavoriteLists={refetchFavoriteLists}
              defaultList={defaultList}
              defaultListItems={
                isFavoriteListItemError ? [] : favoriteListItems?.data
              }
              refetchDefaultList={refetchFavoriteListItems}
            />
          ) : null}

          {featuredToursData ? (
            <FeaturedTours
              isRedirectingSet={isRedirectingSet}
              data={featuredToursData}
              favoriteLists={favoriteLists}
              refetchFavoriteLists={refetchFavoriteLists}
              defaultList={defaultList}
              defaultListItems={
                isFavoriteListItemError ? [] : favoriteListItems?.data
              }
              refetchDefaultList={refetchFavoriteListItems}
            />
          ) : null}
          {preferredThemes ? (
            <MostPreferredThemes
              tabs={preferredThemes?.categories}
              items={preferredThemes.items}
              name={preferredThemes.title}
              content={preferredThemes?.content}
              link={preferredThemes.links}
            />
          ) : null}
        </LandingContainer>
        <WhySeturContainer>
          <WhyBuyingFromSetur
            items={whySeturData}
            label={whyBuyingFromSeturData?.title || ''}
            description={whyBuyingFromSeturData?.subText || ''}
            image={whyBuyingFromSeturData?.images?.[0]}
          />
        </WhySeturContainer>
        <SeoTextContainer>
          <HomePageSeo text={resMainPageSeo?.content} />
        </SeoTextContainer>
        <MobileAppRedirection
          imageUrl={
            mobileAppPromotions?.images
              ? mobileAppPromotions.images?.[0]?.url
              : ''
          }
          title={mobileAppPromotions?.title || ''}
          detail={mobileAppPromotions?.text || ''}
          appStoreUrl='https://apps.apple.com/tr/app/setur-otel-tur-tatil/id1326016062'
          playStoreUrl='https://play.google.com/store/apps/details?id=com.app.setur'
        />
      </Container>
      {isBookerLoading || isRedirecting ? (
        <GeneralLoader shouldScroll={false} />
      ) : null}
    </>
  );
};
export default memo(Index);
